import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { TEuDdpPricingPlan } from 'redux/reducers/euDdp/types';
import { searchPricingPlansRequest } from 'redux/reducers/euDdp/reducer';
import {
	selectPricingPlansSearch,
	selectPricingPlansSearchLoading,
} from 'redux/reducers/euDdp/selectors';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { PricingPlanDialog } from './PricingPlanDialog';

const EDIT_ACTION = 'Edit';

export const EuDdpFees: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [pricingPlanToEdit, setPricingPlanToEdit] = useState<TEuDdpPricingPlan | null>(null);

	const feesData = useSelector(selectPricingPlansSearch);
	const feesLoading = useSelector(selectPricingPlansSearchLoading);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TEuDdpPricingPlan>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const loadFees = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			searchPricingPlansRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadFees();
	}, [loadFees]);

	const handleAddNew = useCallback(() => {
		setPricingPlanToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				const row = feesData?.data?.find((p) => p.id === id) ?? null;
				setPricingPlanToEdit(row);
				setDialogOpen(true);
			}
		},
		[feesData],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setPricingPlanToEdit(null);
		loadFees();
	}, [loadFees]);

	return (
		<>
			<PricingPlanDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				pricingPlanToEdit={pricingPlanToEdit}
			/>
			<EasTableWithFilter<TEuDdpPricingPlan>
				tableStructure={currentTableStructure}
				items={feesData}
				itemsIsLoading={feesLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / EU DDP / <b>Fees</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add New Fee
					</Button>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
